import { getOrDefault } from 'libs/PagingParser'
import {
  always,
  assoc,
  compose,
  filter,
  gt,
  ifElse,
  includes,
  isNil,
  join,
  length,
  lensPath,
  lensProp,
  map,
  over,
  path,
  pick,
  prop,
  propEq,
  set,
  view,
  when,
} from 'ramda'

import { mapPriority } from 'app/components/notificationBadge/common'
import {
  LINE_VALUE,
  LINKEDIN_VALUE,
  TWITTER_VALUE,
} from 'app/constants/channel'
import { TYPE_POLL } from 'app/constants/messageType'
import { GROUP_STATUS, INDIVIDUAL_STATUS } from 'app/constants/recipient'
import { SCOPE_GENERAL } from 'app/constants/scope'
import { removeDoNotTranslateStyle } from 'commons/form'
import { createMoment } from 'commons/useMessageExpiration'
import { isEmptyOrNil } from 'libs/empty'
import momentTimezone from 'moment-timezone'
import { normalizeEmptyParagraphs } from './tiptapEditor/TiptapUtils'

export const viewLogo = view(lensPath(['info', 'logo']))
export const viewName = view(lensPath(['name']))

const mapRecipientToApi = (recipient) => {
  if (recipient) {
    const subscribers = recipient.subscribers.map((subscriber) => subscriber.id)
    return {
      entityType: gt(length(subscribers), 0) ? INDIVIDUAL_STATUS : GROUP_STATUS,
      entityIds: recipient.subGroups,
      subscriberIds: subscribers,
    }
  }
}

const checkDateOrUndefined = (date, timezone) => {
  if (isNil(date)) {
    return undefined
  }
  return momentTimezone.tz(date, timezone).utc().format()
}

export const getMessageRatioPayload = (values) => {
  const groupIds = values.recipients.subGroups

  const subscriberIds = values.recipients.subscribers.map(
    (subscriber) => subscriber.id,
  )
  const topics = values.topics
  const channels = values.channels.channelIds
  return {
    subgroupIds: join(',', groupIds),
    subscriberIds: join(',', subscriberIds),
    topicIds: compose(join(','), map(prop('id')))(topics),
    channels: compose(join(','))(channels),
  }
}

const getPollOptions = (isDraft = false) =>
  compose(
    map(
      when(
        (item) => isEmptyOrNil(prop('imagePath', item)),
        assoc('imagePath', null),
      ),
    ),
    filter((item) => {
      if (isDraft) {
        return (
          !isEmptyOrNil(prop('title', item)) ||
          !isEmptyOrNil(prop('imagePath', item))
        )
      }
      return !isEmptyOrNil(prop('title', item))
    }),
  )
export const mapToApi =
  (formModel, options = {}) =>
  (translations) => {
    const {
      mes_5281_save_draft_for_polls = false,
      isDraft = false,
      draftId = undefined,
    } = options
    let dataMapping = {
      type: formModel.notiType,
      priority: formModel.priority,
      translations: map(
        over(lensProp('body'), removeDoNotTranslateStyle),
        translations,
      ),
      date: checkDateOrUndefined(
        formModel.schedule,
        formModel.scheduleTimezone,
      ),
      scheduleTimezone: formModel.scheduleTimezone,
      remindDate: checkDateOrUndefined(
        formModel.reminder,
        formModel.reminderTimezone,
      ),
      reminderTimezone: formModel.reminderTimezone,
      address: formModel.location?.suggestion,
      latitude: formModel.location?.lat?.toString(),
      longitude: formModel.location?.lng?.toString(),
      startTime: checkDateOrUndefined(
        formModel.eventStartDateTime,
        formModel.eventTimezone,
      ),
      endTime: checkDateOrUndefined(
        formModel.eventEndDateTime,
        formModel.eventTimezone,
      ),
      startTimezone: getOrDefault(undefined)(() => formModel.eventTimezone),
      endTimezone: getOrDefault(undefined)(() => formModel.eventTimezone),
      timezone: getOrDefault(undefined)(() => formModel.eventTimezone),
      allowConversation: formModel.allowConversation,
      pageIds: formModel.channels.pageIds,
      topicIds: compose(map(prop('id')))(formModel.topics),
      twitter: includes(TWITTER_VALUE, formModel.channels.channelIds),
      linkedin: includes(LINKEDIN_VALUE, formModel.channels.channelIds),
      line: includes(LINE_VALUE, formModel.channels.channelIds),
      isTtsEnabled: formModel.isTtsEnabled || false,
      isSharingEnabled: formModel.isSharingEnabled || false,
      ...mapRecipientToApi(formModel.recipients),
      voucherId: formModel.voucher,
      channels: formModel.channels.channelIds,
      shortName: formModel.shortName,
      displayHistory: formModel.isDisplayHistory,
      expirationTime:
        formModel.expirationTime === 0 ? undefined : formModel.expirationTime,
      mediaSocials: getOrDefault(undefined)(() =>
        map(
          pick(['url', 'isSelected', 'socialType', 'type', 'name']),
          prop('mediaSocials', formModel),
        ),
      ),
      messageRatio: getMessageRatioPayload(formModel),
      scope: SCOPE_GENERAL,
      requestId: formModel.requestId,
      ...(!isNil(draftId) ? { draftId } : {}),
    }

    if (formModel.notiType === TYPE_POLL) {
      const endDate =
        mes_5281_save_draft_for_polls && isDraft
          ? isEmptyOrNil(formModel.pollEndDateTime)
            ? ''
            : checkDateOrUndefined(
                formModel.pollEndDateTime,
                formModel.pollTimezone,
              )
          : isEmptyOrNil(formModel.pollEndDateTime)
            ? null
            : {
                dateTime: checkDateOrUndefined(
                  formModel.pollEndDateTime,
                  formModel.pollTimezone,
                ),
                timeZone: formModel.pollTimezone,
              }

      dataMapping = {
        ...dataMapping,
        poll: {
          type: 'single',
          endDate,
          isAnonymousVote: false,
          question: formModel.question,
          additionalContext: getOrDefault(undefined)(
            () => formModel.additionalContext,
          ),
          options: getPollOptions(isDraft)(formModel.options),
        },
      }
    }
    return dataMapping
  }

export const mapFromApi = (initialModel) => {
  return {
    topics: filter(propEq('isSelected', true))(initialModel.topics),
    recipients: initialModel.recipients,
    channels: {
      channelIds: initialModel.channels,
      pageIds: map(prop('id'), initialModel.pageIds),
    },
    title: initialModel.title,
    body: initialModel.body,

    location: {
      suggestion: initialModel.event.address,
      lng: initialModel.event.longitude,
      lat: initialModel.event.latitude,
      timeZoneId: initialModel.event.timezone || undefined,
    },

    allowConversation: initialModel.allowConversation,
    isTtsEnabled: initialModel.isTtsEnabled,
    isSharingEnabled: initialModel.isSharingEnabled,
    isDisplayHistory: initialModel.displayHistory,
    expirationTime: initialModel.expirationTime,

    notiType: initialModel.notiType,
    priority: initialModel.priority,

    eventStartDateTime: initialModel.event?.startDateTime,
    eventEndDateTime: initialModel.event?.endDateTime,
    eventTimezone: initialModel.event?.startTimezone,

    voucher: initialModel.voucher?.id ?? undefined,

    mediaSocials: map(
      ifElse(
        propEq('isSelected', 1),
        set(lensProp('isSelected'), true),
        set(lensProp('isSelected'), false),
      ),
      prop('mediaSocials', initialModel),
    ),

    sendOptions: initialModel.sendOptions,
    reminderChecked: isNil(initialModel.reminderDateTime),
    schedule: initialModel.scheduleDateTime,
    scheduleTimezone: initialModel.scheduleTimezone,
    reminder: initialModel.reminderDateTime,
    reminderTimezone: initialModel.reminderTimezone,
  }
}

export const mapToPreview = (place, model) => {
  return isNil(model)
    ? undefined
    : {
        placeLogo: viewLogo(place),
        placeName: viewName(place),
        title: model.title,
        body: removeDoNotTranslateStyle(normalizeEmptyParagraphs(model.body)),
        description: model.description,
        type: model.notiType,
        priority: mapPriority(model.priority),
        recipients: model.recipients,
        channels: model.channels,
        event: {
          location: model.location.suggestion,
          startDateTime: model.eventStartDateTime,
          startTimezone: model.eventTimezone,
          endDateTime: model.eventEndDateTime,
          endTimezone: model.eventTimezone,
        },
        allowConversation: when(isNil, always(false))(model.allowConversation),
        isTtsEnabled: when(isNil, always(false))(model.isTtsEnabled),
        timezone: compose(
          when(isNil, always(undefined)),
          prop('id'),
        )(model.timezone),
        scheduleStartDate: compose(
          when(isNil, always(undefined)),
          prop('schedule'),
        )(model),
        voucher: compose(
          when(isNil, always(undefined)),
          prop('voucher'),
        )(model),
        topics: compose(when(isNil, always([])), prop('topics'))(model),
        expirationDate:
          model.expirationTime === 0
            ? undefined
            : createMoment(undefined, path(['info', 'timezone'], place))
                .add(model.expirationTime, 'seconds')
                .toISOString(),
        options: model.options,
        pollEndDateTime: model.pollEndDateTime,
        pollTimezone: model.pollTimezone,
        question: model.question,
        additionalContext: model.additionalContext,
        mediaSocials: model.mediaSocials,
      }
}

export const toApiReminderDate = 'remindDate'
export const fromApiReminderDate = 'reminderDate'
